'use client';

import React, { useCallback, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '@/components/ui/AppIcon';
import { ACCEPTED_FORMATS, MAX_FILE_SIZE_MB } from './config';

export interface LocalFile {
  id: string;
  file: File;
  /** Object URL for image previews (empty for RAW files). */
  preview: string;
}

const RAW_EXTENSIONS = ['.arw', '.cr2', '.cr3', '.nef', '.nrw', '.dng', '.raf', '.pef', '.orf', '.rw2', '.srw', '.3fr', '.sr2', '.raw', '.tiff', '.tif'];

export function isRawFile(name: string): boolean {
  const ext = '.' + (name.split('.').pop()?.toLowerCase() || '');
  return RAW_EXTENSIONS.includes(ext);
}

interface UploadZoneProps {
  onFilesAdded: (files: LocalFile[]) => void;
  onError: (message: string) => void;
  maxFiles?: number;
  currentCount?: number;
  minFiles?: number;
  /** Compact mode is used for the secondary reference upload area. */
  compact?: boolean;
  label?: string;
}

export default function UploadZone({
  onFilesAdded,
  onError,
  maxFiles = 300,
  currentCount = 0,
  minFiles,
  compact = false,
  label,
}: UploadZoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const validate = (file: File): string | null => {
    if (file.name.toLowerCase().endsWith('.zip')) {
      return 'ZIP uploads are not allowed. Please upload individual image files.';
    }
    const ext = '.' + (file.name.split('.').pop()?.toLowerCase() || '');
    if (!ACCEPTED_FORMATS.includes(ext)) {
      return `Unsupported format: ${file.name}`;
    }
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      return `${file.name} exceeds the ${MAX_FILE_SIZE_MB}MB limit.`;
    }
    return null;
  };

  const processFiles = useCallback(
    (fileList: FileList | File[]) => {
      const files = Array.from(fileList);
      const accepted: LocalFile[] = [];

      for (const file of files) {
        if (currentCount + accepted.length >= maxFiles) {
          onError(`Maximum ${maxFiles} files allowed.`);
          break;
        }
        const err = validate(file);
        if (err) {
          onError(err);
          continue;
        }
        accepted.push({
          id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          file,
          preview: isRawFile(file.name) ? '' : URL.createObjectURL(file),
        });
      }

      if (accepted.length > 0) {
        onFilesAdded(accepted);
      }
    },
    [currentCount, maxFiles, onError, onFilesAdded]
  );

  return (
    <motion.div
      onDragOver={(e) => {
        e.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        setIsDragging(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragging(false);
        processFiles(e.dataTransfer.files);
      }}
      onClick={() => inputRef.current?.click()}
      animate={{ scale: isDragging ? 1.01 : 1 }}
      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
      className={`relative cursor-pointer rounded-2xl border-2 border-dashed text-center transition-colors duration-200 ${
        compact ? 'p-5' : 'p-6'
      } ${
        isDragging
          ? 'border-purple-400 bg-purple-500/[0.06]'
          : 'border-white/[0.12] bg-white/[0.015] hover:border-purple-500/40 hover:bg-white/[0.03]'
      }`}
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPTED_FORMATS.join(',')}
        onChange={(e) => {
          if (e.target.files) processFiles(e.target.files);
          e.currentTarget.value = '';
        }}
        className="hidden"
      />

      <motion.div
        animate={{ y: isDragging ? -4 : 0 }}
        className={`mx-auto rounded-2xl bg-gradient-to-br from-purple-500/15 to-purple-500/5 flex items-center justify-center ${
          compact ? 'w-10 h-10 mb-2' : 'w-12 h-12 mb-2.5'
        }`}
      >
        <Icon name="CloudArrowUpIcon" size={compact ? 20 : 26} variant="outline" className="text-purple-300" />
      </motion.div>

      <p className={`font-semibold text-white ${compact ? 'text-sm' : 'text-base'}`}>
        {label || 'Drag & drop your images here'}
      </p>
      <p className={`text-gray-400 ${compact ? 'text-xs mt-0.5' : 'text-xs mt-0.5'}`}>
        or <span className="text-purple-300 font-semibold">click to browse</span>
      </p>

      {!compact && (
        <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[11px] text-gray-500">
          <span>JPG · PNG · TIFF · RAW (CR2, NEF, ARW, DNG…)</span>
          <span className="hidden sm:inline">•</span>
          {typeof minFiles === 'number' && <span>Min {minFiles} files</span>}
          <span className="hidden sm:inline">•</span>
          <span>Max {maxFiles} files</span>
          <span className="hidden sm:inline">•</span>
          <span>Up to {MAX_FILE_SIZE_MB}MB each</span>
        </div>
      )}
    </motion.div>
  );
}
